import React, { CSSProperties } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import classNames from 'clsx';
import { ChevronRightRounded } from '@mui/icons-material';

import { SIZE_LARGE, useChannelConfig } from '../../constants/carousels';
import { MAIN_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { VIEW_ALL } from '@/modules/common/components/Player/constants/customEvents';

import { ChannelType } from '@/modules/common/types/channel';

import { usePlayerWidget } from '@/modules/common/components/Player/helpers';
import { isVisibleInConfig } from '@/modules/common/helpers/configuration';
import { useTranslate } from '@/modules/common/helpers/i18n';

import Carousel, { Skeleton as CarouselSkeleton } from '../Carousel/Carousel';
import CarouselLogo from '../CarouselLogo/CarouselLogo';
import { Stack, Typography } from '@/mui/components';

import styles from './VerticalCarousel.module.scss';

type Props = {
  bg: string;
  bgLogo: string;
  titleColor: string;
  descColor: string;
  vidTitleColor: string;
  vidInfoColor: string;
  channel: ChannelType;
};

function VerticalCarousel({ bg, bgLogo, titleColor, descColor, vidTitleColor, vidInfoColor, channel }: Props) {
  const location = useLocation();
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);
  const configByChannel = useChannelConfig();
  const titleConfig = configByChannel.title;
  const viewAllConfig = configByChannel.viewAll;
  const t = useTranslate();

  return (
    <div
      className={styles.Wrapper}
      data-a="channel-type-default"
      aria-label={t.videosByChannel.text()}
      style={
        {
          '--sc-bg-color': bg,
          '--sc-logo-bg-color': bgLogo,
          '--sc-title-color': titleColor,
          '--sc-desc-color': descColor,
          '--sc-vid-title-color': vidTitleColor,
          '--sc-vid-info-color': vidInfoColor,
        } as CSSProperties
      }
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignContent="center"
        alignItems="center"
        className={styles.TitleWrapper}
      >
        {isVisibleInConfig(titleConfig.visible) ? (
          <Typography
            style={{
              fontFamily: titleConfig.fontFamily,
            }}
            title={channel.title}
            rows={1}
            variant={titleConfig.typography ?? 'inherit'}
            color={titleConfig.color ?? 'inherit'}
            fontWeight={titleConfig.fontWeight ?? 400}
            component={RouterLink}
            to={`/channel/${channel.id}${location.search}`}
            className={classNames(styles.Title, {
              [styles.Title___default]: !titleConfig.typography,
            })}
            data-a="channel-title"
          >
            {channel.title}
          </Typography>
        ) : (
          <div />
        )}
        {isVisibleInConfig(viewAllConfig.visible) ? (
          <Typography
            style={{
              fontFamily: viewAllConfig.fontFamily,
            }}
            variant={viewAllConfig.typography ?? 'inherit'}
            fontWeight={viewAllConfig.fontWeight ?? 400}
            color={viewAllConfig.color ?? 'inherit'}
            component={RouterLink}
            to={`/channel/${channel.id}${location.search}`}
            title={t.allVideosInTitle.text({
              title: channel.title,
            })}
            data-a="channel-title-view-all"
            onClick={() => {
              LWP.sendCustomEvent(VIEW_ALL);
            }}
            rows={1}
            className={styles.ViewAll}
          >
            {t.viewAll.text()}
            <ChevronRightRounded className={styles.ViewAllIcon} />
          </Typography>
        ) : (
          <div />
        )}
      </Stack>
      <div className={styles.CarouselWrapper}>
        {channel.size !== SIZE_LARGE && channel.logo && <CarouselLogo url={channel.logo} />}
        <div className={styles.Carousel}>
          {channel.playlist.length ? <Carousel channel={channel} /> : <CarouselSkeleton channel={channel} />}
        </div>
      </div>
    </div>
  );
}

export default VerticalCarousel;
